"use client";
import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";

export default function Hero() {
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const cardRef = useRef(null);
  const [showHubSpotEmbed, setShowHubSpotEmbed] = useState(false);
  const HUBSPOT_MEETING_URL = process.env.NEXT_PUBLIC_HUBSPOTAPI_KEY;

  const utmParams = {
    ref: "riskintel-hero",
    utm_source: "website",
    utm_medium: "cta",
    utm_campaign: "riskintel",
    utm_content: "riskintel__hero__book_a_demo",
  };

  const addUtmToUrl = (url) => {
    if (!url) return url;
    const params = new URLSearchParams(utmParams);
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${params.toString()}`;
  };

  const openHubSpotBooking = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setShowHubSpotEmbed(true);
    if (typeof window !== "undefined") {
      const currentUrl = new URL(window.location.href);
      Object.entries(utmParams).forEach(([key, value]) => {
        currentUrl.searchParams.set(key, value);
      });
      window.history.replaceState(
        {},
        "",
        currentUrl.pathname + "?" + currentUrl.searchParams.toString()
      );
    }
    if (typeof document !== "undefined") {
      document.body.style.overflow = "hidden";
    }
  };

  const closeHubSpotBooking = () => {
    setShowHubSpotEmbed(false);
    if (typeof window !== "undefined") {
      const currentUrl = new URL(window.location.href);
      Object.keys(utmParams).forEach((key) => {
        currentUrl.searchParams.delete(key);
      });
      const newQuery = currentUrl.searchParams.toString();
      window.history.replaceState(
        {},
        "",
        currentUrl.pathname + (newQuery ? `?${newQuery}` : "")
      );
    }
    if (typeof document !== "undefined") {
      document.body.style.overflow = "unset";
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && showHubSpotEmbed) closeHubSpotBooking();
    };
    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleEscape);
      return () => window.removeEventListener("keydown", handleEscape);
    }
  }, [showHubSpotEmbed]);

  // AetherFlow particle field
  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const PARTICLE_FILL = "rgba(160, 138, 255, 0.7)";
    const lineColor = (op) => `rgba(160, 138, 255, ${op})`;
    const lineColorHot = (op) => `rgba(200, 180, 255, ${op})`;

    const MAX_V = 0.18;
    const DENSITY_AREA = 16000;
    const MOUSE_RADIUS = 160;
    const PUSH_GAIN = 2.2;
    const OPACITY_DIVISOR = 20000;
    const OPACITY_BASE_IDLE = 0.28;
    const OPACITY_BASE_HOT = 0.70;
    const OPACITY_CULL = 0.02;

    let width = 0;
    let height = 0;
    let particles = [];
    const mouse = { x: null, y: null };
    let rafId = null;
    let lastInitWidth = 0;

    const makeParticle = (yMin) => ({
      x: Math.random() * width,
      y: yMin + Math.random() * (height - yMin),
      vx: (Math.random() - 0.5) * MAX_V * 2,
      vy: (Math.random() - 0.5) * MAX_V * 2,
      size: 1 + Math.random(),
    });

    const reinit = () => {
      const rect = host.getBoundingClientRect();
      const prevHeight = height;
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));
      canvas.width = width;
      canvas.height = height;

      const widthChanged = Math.abs(width - lastInitWidth) > 24;
      const firstInit = particles.length === 0;

      if (firstInit || widthChanged) {
        const count = Math.max(20, Math.floor((width * height) / DENSITY_AREA));
        particles = new Array(count);
        for (let i = 0; i < count; i++) particles[i] = makeParticle(0);
        lastInitWidth = width;
      } else {
        const target = Math.max(20, Math.floor((width * height) / DENSITY_AREA));
        const yMin = Math.min(prevHeight, height);
        while (particles.length < target) particles.push(makeParticle(yMin));
        while (particles.length > target) particles.pop();
      }
    };

    const step = () => {
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (mouse.x !== null) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const radius = MOUSE_RADIUS + p.size;
          if (dist < radius && dist > 0) {
            const push = ((MOUSE_RADIUS - dist) / MOUSE_RADIUS) * PUSH_GAIN;
            p.x += (dx / dist) * push;
            p.y += (dy / dist) * push;
          }
        }
        p.x += p.vx;
        p.y += p.vy;
        if (p.x <= 0 || p.x >= width) p.vx *= -1;
        if (p.y <= 0 || p.y >= height) p.vy *= -1;
        if (p.x < 0) p.x = 0;
        if (p.x > width) p.x = width;
        if (p.y < 0) p.y = 0;
        if (p.y > height) p.y = height;
      }

      const mr2 = MOUSE_RADIUS * MOUSE_RADIUS;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        let pHot = false;
        if (mouse.x !== null) {
          const mdx = p.x - mouse.x;
          const mdy = p.y - mouse.y;
          pHot = mdx * mdx + mdy * mdy < mr2;
        }
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const dSq = dx * dx + dy * dy;
          if (dSq >= OPACITY_DIVISOR) continue;
          const fade = 1 - dSq / OPACITY_DIVISOR;
          let hot = pHot;
          if (!hot && mouse.x !== null) {
            const qmdx = q.x - mouse.x;
            const qmdy = q.y - mouse.y;
            hot = qmdx * qmdx + qmdy * qmdy < mr2;
          }
          const op = fade * (hot ? OPACITY_BASE_HOT : OPACITY_BASE_IDLE);
          if (op < OPACITY_CULL) continue;
          ctx.strokeStyle = hot ? lineColorHot(op) : lineColor(op);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(q.x, q.y);
          ctx.stroke();
        }
      }

      ctx.fillStyle = PARTICLE_FILL;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      rafId = requestAnimationFrame(step);
    };

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (x < 0 || x > rect.width || y < 0 || y > rect.height) {
        mouse.x = null;
        mouse.y = null;
      } else {
        mouse.x = x;
        mouse.y = y;
      }
    };
    const onMouseOut = () => {
      mouse.x = null;
      mouse.y = null;
    };

    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(reinit) : null;
    if (ro) ro.observe(host);
    else window.addEventListener("resize", reinit);

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("mouseout", onMouseOut);

    reinit();
    rafId = requestAnimationFrame(step);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", reinit);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  // Highlight card reveal — slide in, then expand metrics
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      card.classList.add("is-visible", "is-expanded");
      return;
    }
    let timer = null;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            card.classList.add("is-visible");
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => card.classList.add("is-expanded"), 450);
          } else {
            if (timer) {
              clearTimeout(timer);
              timer = null;
            }
            card.classList.remove("is-visible", "is-expanded");
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(card);
    return () => {
      if (timer) clearTimeout(timer);
      io.disconnect();
    };
  }, []);

  return (
    <div className="dark-stage relative overflow-hidden hero-bg text-white">
      <div
        ref={hostRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
      >
        <canvas ref={canvasRef} className="block h-full w-full" />
      </div>

      {/* HERO */}
      <section id="top" className="relative z-10 pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="container-c relative z-10">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-line-dark bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-blue"></span>
              a Cloudastra  Product
            </div>

            <h1 className="font-display text-[40px] leading-[1.04] tracking-tightest text-balance sm:text-[54px] md:text-[64px]">
              Screen the name. <span className="text-purple">Walk the network.</span> Defend every decision.
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-[17px] leading-relaxed text-white/75 text-pretty md:text-[19px]">
              RiskIntel runs audit-grade screening across sanctions, PEPs, 13 Indian regulators, GST, and live court records, then walks the director network behind every match. So the customer who looks clean really is.
            </p>

            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={openHubSpotBooking}
                className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-blue px-7 text-[15px] font-semibold text-white shadow-cta transition hover:bg-blue-hover"
              >
                Book a demo
                <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="https://riskintel.cloudastra.co/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-14 items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-7 text-[15px] font-semibold text-white backdrop-blur transition hover:bg-white/10"
              >
                Explore the product
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHT STRIP */}
      <section className="relative z-10 py-12 md:py-16">
        <div className="container-c">
          <div
            ref={cardRef}
            className="reveal-card glass-card relative overflow-hidden rounded-2xl"
          >
            <div className="px-6 py-3.5 text-center text-[12.5px] text-white/55 md:px-8">
              Trusted by compliance teams across India&apos;s fintechs, banks, and regulated entities.
            </div>
            <div className="reveal-metrics">
              <div className="stat-grid grid grid-cols-1 md:grid-cols-3">
                <div className="stat-col px-6 py-5 text-center md:px-8 md:py-6">
                  <p className="font-display text-[26px] font-bold tracking-tightest text-blue md:text-[28px]">70%</p>
                  <p className="mt-1.5 font-display text-[14px] font-semibold text-white">Faster compliance reviews</p>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-white/55">Smaller queues, fewer false positives.</p>
                </div>
                <div className="stat-col px-6 py-5 text-center md:px-8 md:py-6">
                  <p className="font-display text-[26px] font-bold tracking-tightest text-blue md:text-[28px]">13</p>
                  <p className="mt-1.5 font-display text-[14px] font-semibold text-white">Indian regulators, 300K+ entities</p>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-white/55">Deepest regulatory coverage built for India.</p>
                </div>
                <div className="stat-col px-6 py-5 text-center md:px-8 md:py-6">
                  <p className="font-display text-[26px] font-bold tracking-tightest text-blue md:text-[28px]">4-hop</p>
                  <p className="mt-1.5 font-display text-[14px] font-semibold text-white">Director network mapping</p>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-white/55">Surface risks no global tool will.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showHubSpotEmbed && (
        <div
          id="hubspot-embed-section"
          className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center"
          onClick={closeHubSpotBooking}
        >
          <div
            className="relative w-full overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:mx-4 sm:max-w-4xl sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeHubSpotBooking}
              aria-label="Close booking calendar"
              className="absolute top-2 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md text-gray-700 transition-colors hover:bg-gray-100"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="overflow-hidden" style={{ marginTop: "-48px" }}>
              <iframe
                src={addUtmToUrl(HUBSPOT_MEETING_URL)}
                title="Schedule a Call"
                className="w-full border-0"
                style={{ height: "calc(85vh + 48px)", border: "none" }}
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
